/**
 * syncEngine.ts — live-document auto-save watcher.
 *
 * HISTORY: this module used to mirror every paragraph edit to the standalone
 * "DocService" block store as an OOXML DLL. DocService was decommissioned
 * (its formatting store is redundant with the Save-gated ``section_ooxml``
 * snapshots that the download rebuild composes). This module was rewritten to
 * REUSE the same event/debounce/pause machinery to drive AUTO-SAVE instead:
 * whenever the user edits the live doc, the affected section's markdown
 * (``draft_full``) + OOXML (``section_ooxml``) are persisted straight to the
 * Mongo SSOT — the same write the Save button performs, scoped to one section.
 *
 * Public API (consumed by VariantCShell / useSyncStatus / useDraftFlow /
 * wordWriter — keep stable):
 *   - initSyncEngine(getRunId) → cleanup    start/stop the watcher
 *   - pauseSync() / resumeSync(runId?)       suspend around programmatic writes
 *   - subscribeSyncStatus(cb) / isSyncing()  "saving…" indicator state
 *   - subscribeParagraphEdits(cb)            writer edits (paragraph events)
 *   - markSectionChanged(runId, moduleId)    an add-in write changed a section
 *   - markCursorSectionChanged()             … the section the cursor is in
 *   - markSectionNumbersChanged(numbers)     … sections by dotted number
 *   - flushPendingAutoSaves()                save touched sections now (Load)
 *   - subscribeSaveNotice(cb)                sections an auto-save could not save
 *   - _isLikelyLoadingPlaceholderImage(...)   placeholder-image guard (wordWriter)
 *
 * Section-scoping: the watcher has no paragraph→section map of its own. On each
 * settled edit it resolves the CURSOR's owning section via the ``ASTHRA_SEC_*``
 * bookmarks (``detectCursorSectionLight``) and saves that section (plus any it
 * accumulated from earlier triggers in the same window). The section-scoped
 * save hash-compares against the SSOT, so a redundant save is a cheap no-op.
 *
 * Add-in writes (an approved chat edit, an inserted fragment or reference)
 * raise Word events too, but those are dropped while the write queue owns the
 * document. Their callers mark the section explicitly instead
 * (``markSectionChanged``); the save then waits for the queue to go idle and
 * runs through the same debounce, so a burst of approvals is one save.
 */

import { createParagraphSectionCache } from "./cursorSectionCache";
import { isLiveDocLoading } from "./liveDocLoadState";
import { enqueueWordWrite, isWordWriteQueueBusy, whenWordWriteQueueIdle } from "./wordWriteQueue";

// ---------------------------------------------------------------------------
// Placeholder-image guard (also imported by wordWriter — keep exported)
// ---------------------------------------------------------------------------

// A freshly-inserted inline picture that Word-for-the-web hasn't finished
// loading reads back through getBase64ImageSrc() as a tiny "loading" BITMAP
// (~850-byte image/bmp), NOT the real figure bytes. Capturing that into a
// section_ooxml snapshot would bake a grey box into the saved formatting, so
// the OOXML capture DEFERS while a placeholder is detected (see wordWriter's
// readSectionSubtreeOoxml). Bounded so a genuine (rare) small BMP can't defer
// forever.
const _PLACEHOLDER_IMAGE_MAX_BYTES = 2048; // real CSR figures are JPEG/PNG, ≫2 KB

/** Decoded byte length of a base64 payload (no data-URI prefix). */
export function _base64ByteLength(base64: string): number {
  const len = base64.length;
  if (len === 0) return 0;
  const pad = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((len * 3) / 4) - pad;
}

/** True when captured inline-picture bytes look like Word-for-the-web's
 *  still-loading placeholder bitmap (a tiny BMP) rather than a real figure. */
export function _isLikelyLoadingPlaceholderImage(base64: string, format: string): boolean {
  return format === "bmp" && _base64ByteLength(base64) > 0 && _base64ByteLength(base64) <= _PLACEHOLDER_IMAGE_MAX_BYTES;
}

// ---------------------------------------------------------------------------
// Timing constants
// ---------------------------------------------------------------------------

// Idle window after the last edit trigger before an auto-save fires. Long
// enough that a burst of typing (or of approvals) settles into one save.
const DEBOUNCE_MS = 3000;

// While WordApi 1.6 paragraph events are active, caret-only selection changes
// are throttled to this heartbeat — real content edits already fire paragraph
// events, but the heartbeat still backstops toolbar formatting changes that
// mutate a paragraph without a content event.
const SELECTION_HEARTBEAT_MS = 15000;

// Minimum gap between two cursor-section resolves while paragraph events
// stream in (one per keystroke).
const CURSOR_RESOLVE_THROTTLE_MS = 1000;

type SyncTrigger = "paragraph" | "selection";

/** A Load or a queued add-in write currently owns the live document. */
function _wordBusy(): boolean {
  return isWordWriteQueueBusy() || isLiveDocLoading();
}

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let _paused = false;
let _pendingTimer: ReturnType<typeof setTimeout> | null = null;
let _lastSelectionArmAt = 0;

// Sections touched since the last completed save (dotted numbers, e.g. "9.2").
const _touchedSections = new Set<string>();

// Sections an add-in write changed, by module id → the run they belong to.
// Kept across pause/resume (unlike ``_touchedSections``): an approval must not
// be dropped because a Load or Save paused the watcher in between, and saving a
// section the Load just rewrote is a hash-compare no-op.
const _touchedModules = new Map<string, string>();

// Set by ``initSyncEngine`` while the writer-edit watcher runs; null otherwise.
let _getRunId: (() => string | null) | null = null;
let _resolveModuleId: ResolveModuleId | null = null;
let _cancelIdleWait: (() => void) | null = null;
// The auto-save cycle currently running, if any.
let _inFlightSave: Promise<void> | null = null;

// "saving in flight" flag + subscribers (drives the top-bar Syncing/Synced pill).
let _syncing = false;
const _statusSubscribers = new Set<(syncing: boolean) => void>();

function _setSyncing(next: boolean): void {
  if (_syncing === next) return;
  _syncing = next;
  for (const cb of _statusSubscribers) {
    try {
      cb(next);
    } catch {
      /* a subscriber throwing must never break the save loop */
    }
  }
}

function _nowMs(): number {
  return Date.now();
}

// ---------------------------------------------------------------------------
// Public status API
// ---------------------------------------------------------------------------

/** Subscribe to the auto-save "in flight" flag. Fires immediately with the
 *  current value, then on every change. Returns an unsubscribe fn. */
export function subscribeSyncStatus(cb: (syncing: boolean) => void): () => void {
  _statusSubscribers.add(cb);
  try {
    cb(_syncing);
  } catch {
    /* ignore */
  }
  return () => {
    _statusSubscribers.delete(cb);
  };
}

/** Paragraph events that passed the pause / add-in-write gates, i.e. edits
 *  the writer made. Lets other watchers react to real edits instead of polling
 *  Word on a timer. */
const _paragraphEditListeners = new Set<() => void>();

function _notifyParagraphEdit(): void {
  for (const cb of _paragraphEditListeners) {
    try {
      cb();
    } catch {
      /* a listener throwing must never break the save loop */
    }
  }
}

/** Subscribe to writer edits (WordApi 1.6 paragraph events, only while the
 *  watcher is running). Returns an unsubscribe fn. */
export function subscribeParagraphEdits(cb: () => void): () => void {
  _paragraphEditListeners.add(cb);
  return () => {
    _paragraphEditListeners.delete(cb);
  };
}

/** True while an auto-save is in flight. */
export function isSyncing(): boolean {
  return _syncing;
}

/** Sections the last auto-save could NOT persist, and why — ``null`` once a
 *  save goes through clean. Surfaced next to the Saving/Saved pill so a skipped
 *  section is never silent. */
export interface SaveNotice {
  trackChanges: string[];
  notFound: string[];
  failed: string[];
  /** Changed on the server since it was read — not overwritten. */
  conflicted: string[];
}

let _saveNotice: SaveNotice | null = null;
const _noticeSubscribers = new Set<(notice: SaveNotice | null) => void>();

function _setSaveNotice(next: SaveNotice | null): void {
  if (_saveNotice === null && next === null) return;
  _saveNotice = next;
  for (const cb of _noticeSubscribers) {
    try {
      cb(next);
    } catch {
      /* a subscriber throwing must never break the save loop */
    }
  }
}

/** Drop the notice — a full Save just persisted the whole document. */
export function clearSaveNotice(): void {
  _setSaveNotice(null);
}

/** Subscribe to the auto-save notice. Fires immediately with the current
 *  value, then on every change. Returns an unsubscribe fn. */
export function subscribeSaveNotice(cb: (notice: SaveNotice | null) => void): () => void {
  _noticeSubscribers.add(cb);
  try {
    cb(_saveNotice);
  } catch {
    /* ignore */
  }
  return () => {
    _noticeSubscribers.delete(cb);
  };
}

// ---------------------------------------------------------------------------
// Pause / resume — suspend auto-save around programmatic document writes
// (load, hydration, agent redraft insertion) so we never persist a half-written
// intermediate state or fight the writer.
// ---------------------------------------------------------------------------

export function pauseSync(): void {
  _paused = true;
  if (_pendingTimer !== null) {
    clearTimeout(_pendingTimer);
    _pendingTimer = null;
  }
  console.log("[sync] pauseSync — auto-save suspended");
}

/**
 * Resume auto-save after a pause. ``runId`` is accepted for backward
 * compatibility with the legacy re-seed contract; it is no longer needed (the
 * next real edit re-arms the watcher on its own). Any timer armed by a
 * selection change that fired during the pause window is discarded so a cycle
 * can't run against a still-settling document.
 */
export function resumeSync(_runId?: string | null): void {
  if (_pendingTimer !== null) {
    clearTimeout(_pendingTimer);
    _pendingTimer = null;
  }
  _touchedSections.clear();
  _paused = false;
  console.log("[sync] resumeSync — auto-save re-enabled");
  // Sections an add-in write marked during the pause still need saving.
  if (_touchedModules.size > 0) _scheduleSave();
}

// ---------------------------------------------------------------------------
// Explicit marks — add-in writes whose Word events the watcher ignores
// ---------------------------------------------------------------------------

/** True while the writer-edit watcher runs for ``runId`` — i.e. this document
 *  holds that run's draft and the live document owns it (sync-ready, pointer
 *  "live"). Anything that pushes live text to the SSOT outside the watcher
 *  checks this first, so it never writes over a draft the server still owns. */
export function isAutoSaveActiveFor(runId: string | null | undefined): boolean {
  return !!runId && !!_getRunId && _getRunId() === runId;
}

/** An add-in write changed this section: save it once the write queue is idle
 *  and the debounce settles. Works whether or not the writer-edit watcher is
 *  running. Returns false (nothing scheduled) without a run or module id. */
export function markSectionChanged(runId: string | null | undefined, moduleId: string | null | undefined): boolean {
  if (!runId || !moduleId) return false;
  _touchedModules.set(moduleId, runId);
  _scheduleSave();
  return true;
}

/** True while the writer-edit watcher runs and can map section numbers to
 *  modules — i.e. ``markSectionNumbersChanged`` would schedule something.
 *  Lets a caller skip the Word read that finds the numbers otherwise. */
export function isAutoSaveWatching(): boolean {
  return !!_resolveModuleId && !!_getRunId?.();
}

/** An add-in write changed these sections (dotted numbers, e.g. a bridge
 *  write at a paragraph anchor). Needs the running watcher to map numbers to
 *  module ids — without it the live document doesn't own the draft and there
 *  is nothing to auto-save. Returns true when anything was scheduled. */
export function markSectionNumbersChanged(sectionNumbers: readonly string[]): boolean {
  const runId = _getRunId?.() ?? null;
  if (!runId || !_resolveModuleId) return false;
  let marked = false;
  for (const sectionNumber of sectionNumbers) {
    const moduleId = _resolveModuleId(sectionNumber);
    if (!moduleId) {
      console.warn(`[sync] add-in write: no module id for section ${sectionNumber} — not auto-saved`);
      continue;
    }
    _touchedModules.set(moduleId, runId);
    marked = true;
  }
  if (marked) _scheduleSave();
  return marked;
}

/**
 * Save every section an edit or add-in write touched NOW instead of after the
 * debounce, and wait for it (plus any save already in flight). Load calls this
 * before it pauses the watcher and reads the SSOT, so an approved edit made
 * seconds earlier is in the body Load fetches — otherwise the pre-edit body
 * (e.g. a resolved [MISSING INFO] flag) came back. Bounded by ``timeoutMs`` so
 * a stuck save never blocks the Load; never throws.
 */
export async function flushPendingAutoSaves(timeoutMs = 15000): Promise<void> {
  const work = (async () => {
    if (_inFlightSave) await _inFlightSave;
    if (_pendingTimer !== null) {
      clearTimeout(_pendingTimer);
      _pendingTimer = null;
    }
    if (_cancelIdleWait !== null) {
      _cancelIdleWait();
      _cancelIdleWait = null;
    }
    if (_paused || (_touchedSections.size === 0 && _touchedModules.size === 0)) return;
    await _startAutoSave();
  })();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      console.warn(`[sync] flushPendingAutoSaves: still saving after ${timeoutMs} ms — continuing`);
      resolve();
    }, timeoutMs);
  });
  try {
    await Promise.race([work, timeout]);
  } catch (err) {
    console.warn("[sync] flushPendingAutoSaves failed (non-fatal):", err);
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

/** An add-in write changed the section the cursor is in (e.g. text inserted at
 *  the selection). Needs the running watcher to map the section to a module. */
export function markCursorSectionChanged(): void {
  if (!_getRunId || !_resolveModuleId) return;
  void import("./wordWriter")
    .then(({ detectCursorSectionLight }) => detectCursorSectionLight())
    .then((section) => {
      if (!section) return;
      _touchedSections.add(section);
      _scheduleSave();
    })
    .catch((err: unknown) => {
      console.warn("[sync] cursor-section resolve failed (non-fatal):", err);
    });
}

function _scheduleSave(): void {
  if (_pendingTimer !== null) clearTimeout(_pendingTimer);
  _pendingTimer = setTimeout(() => {
    _pendingTimer = null;
    _fireAutoSave();
  }, DEBOUNCE_MS);
}

// The capture reads the section from Word; if an add-in write grabbed Word
// during the debounce, wait for it rather than read a half-written section.
function _fireAutoSave(): void {
  if (!isWordWriteQueueBusy() && isLiveDocLoading()) {
    // A Load owns the document but the queue is idle: the idle callback would
    // fire straight back into this function. Try again after another debounce.
    _scheduleSave();
    return;
  }
  if (_wordBusy()) {
    if (_cancelIdleWait === null) {
      _cancelIdleWait = whenWordWriteQueueIdle(() => {
        _cancelIdleWait = null;
        _fireAutoSave();
      });
    }
    return;
  }
  void _startAutoSave();
}

/** Run one auto-save cycle, tracked so ``flushPendingAutoSaves`` can wait on
 *  it. Never rejects. */
function _startAutoSave(): Promise<void> {
  const cycle: Promise<void> = _runAutoSave()
    .catch((err: unknown) => {
      console.error("[sync] auto-save cycle exception:", String(err));
    })
    .finally(() => {
      if (_inFlightSave === cycle) _inFlightSave = null;
    });
  _inFlightSave = cycle;
  return cycle;
}

// ---------------------------------------------------------------------------
// Auto-save cycle
// ---------------------------------------------------------------------------

/** Resolve a dotted section number to its module id, using the live spine the
 *  shell holds. ``null`` when the number isn't in the spine — the scoped save
 *  then skips it rather than falling back to a whole-document fetch. */
export type ResolveModuleId = (sectionNumber: string) => string | null;

/** Save every touched section to the SSOT — sections a REAL edit touched (by
 *  dotted number, from paragraph events) plus sections an add-in write marked
 *  (by module id). Grouped per run into one scoped capture: one Word scan and
 *  one bulk write. Best-effort: a failure logs and requeues so the next settle
 *  (or the Save button) retries.
 *
 *  Sections are recorded only by a content event or an explicit mark — never by
 *  the caret merely sitting in a section — so with nothing touched this returns
 *  before any network call. */
async function _runAutoSave(): Promise<void> {
  if (_paused) return;
  if (_touchedSections.size === 0 && _touchedModules.size === 0) return;

  // run id → module ids to save.
  const byRun = new Map<string, Set<string>>();
  const add = (runId: string, moduleId: string) => {
    const set = byRun.get(runId) ?? new Set<string>();
    set.add(moduleId);
    byRun.set(runId, set);
  };
  for (const [moduleId, runId] of _touchedModules) add(runId, moduleId);
  _touchedModules.clear();
  const watcherRunId = _getRunId?.() ?? null;
  for (const sectionNumber of _touchedSections) {
    const moduleId = _resolveModuleId ? _resolveModuleId(sectionNumber) : null;
    if (!watcherRunId || !moduleId) {
      // Not in the live spine (e.g. a heading the run doesn't own) — nothing
      // scoped to save; never widen to /all-sections to find out.
      console.warn(`[sync] auto-save skipped: no module id for section ${sectionNumber}`);
      continue;
    }
    add(watcherRunId, moduleId);
  }
  _touchedSections.clear();
  if (byRun.size === 0) return;

  _setSyncing(true);
  const notice: SaveNotice = { trackChanges: [], notFound: [], failed: [], conflicted: [] };
  try {
    const { captureAndSaveModules } = await import("../variant-c/services/writeBackBridge");
    for (const [runId, moduleIds] of byRun) {
      if (_paused) {
        // A programmatic write started mid-save — requeue and bail.
        for (const moduleId of moduleIds) _touchedModules.set(moduleId, runId);
        continue;
      }
      try {
        const result = await captureAndSaveModules(runId, [...moduleIds], { enqueueWordWrite });
        notice.trackChanges.push(...result.skippedTrackChangesSections);
        notice.notFound.push(...result.skippedMissingSections);
        notice.failed.push(...result.failedSections);
        notice.conflicted.push(...result.conflictedSections);
        if (result.fetchFailed || result.captureFailed) {
          // Nothing (or not everything) was compared — retry on the next settle.
          for (const moduleId of moduleIds) _touchedModules.set(moduleId, runId);
        }
      } catch (err) {
        for (const moduleId of moduleIds) _touchedModules.set(moduleId, runId);
        console.warn(`[sync] auto-save failed for run ${runId}:`, err);
      }
    }
  } finally {
    _setSyncing(false);
  }
  const clean =
    notice.trackChanges.length + notice.notFound.length + notice.failed.length + notice.conflicted.length === 0;
  _setSaveNotice(clean ? null : notice);
}

/** True when the running Word host supports WordApi 1.6 paragraph events
 *  (onParagraphChanged/Added/Deleted). Guarded — false on any uncertainty so
 *  the watcher falls back to selection-only triggering. */
function _wordApi16Supported(): boolean {
  try {
    const reqs = (
      globalThis as {
        Office?: { context?: { requirements?: { isSetSupported?: (n: string, v: string) => boolean } } };
      }
    ).Office?.context?.requirements;
    return typeof reqs?.isSetSupported === "function" && reqs.isSetSupported("WordApi", "1.6") === true;
  } catch {
    return false;
  }
}

/**
 * Start the auto-save watcher for the current run. The caller (VariantCShell)
 * gates this on the same lifecycle conditions the legacy engine used —
 * ``isSyncReady(runId)`` and ``draft_pointer !== "server"`` — so the watcher
 * never fires on placeholder scaffolding or while the server owns the doc.
 * Returns a cleanup fn that removes all handlers.
 */
export function initSyncEngine(
  getRunId: () => string | null = () => null,
  resolveModuleId: ResolveModuleId | null = null,
): () => void {
  console.log(`[sync] initSyncEngine — runId=${getRunId() ?? "none"}`);
  _getRunId = getRunId;
  _resolveModuleId = resolveModuleId;

  let eventsActive = _wordApi16Supported();

  // Only a REAL content edit (a paragraph event) may mark the cursor's section
  // as touched. A selection trigger — including the 15 s heartbeat — only flushes
  // sections an edit already touched; it never adds one, so an unedited document
  // produces no auto-save cycle at all. (The heartbeat's old "backstop for
  // toolbar formatting" role is moot: Save is markdown-only, so formatting never
  // changes the SSOT.) Recorded at arm time, before the debounce, so the
  // section is the one the edit actually landed in.
  //
  // Typing fires a paragraph event per keystroke, and each resolve is a Word
  // round trip (~300 ms on Word for Mac), so it runs on the leading edge of a
  // burst and at most once per CURSOR_RESOLVE_THROTTLE_MS, never two at once.
  //
  // A paragraph already resolved is not asked again: the event names the
  // paragraphs it changed, so steady typing in one paragraph costs no Word
  // round trip at all (see cursorSectionCache).
  let cursorResolveInFlight = false;
  let lastCursorResolveAt = 0;
  const sectionByParagraph = createParagraphSectionCache<string | null>();
  const recordCursorSection = (paragraphIds?: readonly string[]) => {
    if (paragraphIds && paragraphIds.length > 0) {
      const hits = paragraphIds.map((id) => sectionByParagraph.get(id));
      if (hits.every((h) => h.hit)) {
        for (const h of hits) if (h.hit && h.value) _touchedSections.add(h.value);
        return;
      }
    }
    const now = _nowMs();
    if (cursorResolveInFlight || now - lastCursorResolveAt < CURSOR_RESOLVE_THROTTLE_MS) return;
    cursorResolveInFlight = true;
    lastCursorResolveAt = now;
    void import("./wordWriter")
      .then(({ detectCursorSectionLight }) => detectCursorSectionLight())
      .then((section) => {
        if (section) _touchedSections.add(section);
        // The cursor is in the edited paragraph; with several (a paste across
        // sections) the one answer does not hold for all of them.
        if (paragraphIds && paragraphIds.length === 1) sectionByParagraph.set(paragraphIds[0], section);
      })
      .catch((err: unknown) => {
        console.warn("[sync] cursor-section resolve failed (non-fatal):", err);
      })
      .finally(() => {
        cursorResolveInFlight = false;
      });
  };

  const armCycle = (trigger: SyncTrigger, paragraphIds?: readonly string[]) => {
    if (_paused) return;
    // A Load or queued add-in write owns Word: the events it raises are our own
    // inserts (hundreds per section), not user edits. Reacting would start a
    // Word read per event behind the write and then auto-save the section we
    // just wrote. Same trade-off as pauseSync around Load/Save: a user edit
    // made during an add-in write is picked up by the next edit or by Save.
    if (_wordBusy()) {
      // The add-in is rewriting the document: paragraphs may change sections.
      sectionByParagraph.clear();
      return;
    }

    // Throttle caret-only selection triggers to a heartbeat while paragraph
    // events are active — real edits already fire paragraph events.
    if (trigger === "selection" && eventsActive) {
      const now = _nowMs();
      if (now - _lastSelectionArmAt < SELECTION_HEARTBEAT_MS) return;
      _lastSelectionArmAt = now;
    }

    if (trigger === "paragraph") {
      recordCursorSection(paragraphIds);
      _notifyParagraphEdit();
    }

    _scheduleSave();
  };

  const selectionHandler = () => armCycle("selection");
  Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, selectionHandler);
  console.log("[sync] handler registered on DocumentSelectionChanged");

  // WordApi 1.6: paragraph content events fire only on real edits, so typing
  // triggers an immediate section save while caret-only moves cost nothing.
  // Best-effort — any failure flips eventsActive=false so selection triggers
  // arm every change again (legacy fallback). Handlers die with the task pane;
  // cleanup below removes them on an explicit unmount.
  const paragraphEventRemovers: Array<() => void> = [];
  let paragraphEventContext: Word.RequestContext | null = null;
  if (eventsActive) {
    void Word.run(async (context) => {
      const doc = context.document;
      const changed = doc.onParagraphChanged.add((args) => {
        armCycle("paragraph", args?.uniqueLocalIds);
        return Promise.resolve();
      });
      const added = doc.onParagraphAdded.add((args) => {
        armCycle("paragraph", args?.uniqueLocalIds);
        return Promise.resolve();
      });
      const deleted = doc.onParagraphDeleted.add(() => {
        armCycle("paragraph");
        return Promise.resolve();
      });
      await context.sync();
      paragraphEventContext = context;
      paragraphEventRemovers.push(
        () => changed.remove(),
        () => added.remove(),
        () => deleted.remove(),
      );
      console.log("[sync] paragraph events registered (WordApi 1.6)");
    }).catch((err: unknown) => {
      eventsActive = false;
      console.warn("[sync] paragraph event registration failed — selection-only fallback", String(err));
    });
  } else {
    console.log("[sync] WordApi 1.6 unavailable — selection-only triggering");
  }

  return () => {
    // Explicit marks outlive the watcher (they carry their own run id); only
    // drop the pending timer when there is nothing of theirs left to save.
    if (_pendingTimer !== null && _touchedModules.size === 0) {
      clearTimeout(_pendingTimer);
      _pendingTimer = null;
    }
    _touchedSections.clear();
    if (_getRunId === getRunId) {
      _getRunId = null;
      _resolveModuleId = null;
    }
    Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
      handler: selectionHandler,
    });
    if (paragraphEventRemovers.length > 0 && paragraphEventContext) {
      for (const remove of paragraphEventRemovers) remove();
      void paragraphEventContext.sync().catch(() => {
        /* handlers die with the task pane anyway */
      });
    }
    console.log("[sync] cleanup — handlers removed");
  };
}
